// ============================================================================
// Finance view PAGE 1 — Executive Overview (CW-Q). Bryan signed this off
// 2026-08-12; the IA is not re-litigated here.
//
// Eight bands, in Jose's order, each mapping to one section of
// TCS-Finance-View-Page1-queries.sql:
//   1  Road to the first $1m month              §9
//   2  Where we stand — <month>                 §1
//   3  Annual recurring revenue by territory    §3 §4 §5
//   4  Where the margin sits                    §2 §3
//   5  Hospitals — the weekly run rate          §10
//   6  Revenue by account manager               §6
//   7  Money on the table                       §7 §8
//   8  Where the data still fails               §11 §12
// ============================================================================

import { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import {
  ARR_CAVEAT,
  BAND_CLASSES,
  LEVEL_MIX_NOTE,
  MANAGER_VIEW_NOTE,
  SERVICE_LINE_ORDER,
  TERRITORY_COLOUR_NAME,
  TERRITORY_ORDER,
  TERRITORY_STATES,
  TERRITORY_STYLES,
  bandLabel,
  dollarsPerYearToTarget,
  fmtMoney,
  fmtMoneyCompact,
  fmtPct,
  fmtPp,
  groupBy,
  marginBand,
  monthLabel,
  namedManagers,
  rollUp,
  sumToTarget,
  type MarginThresholds,
} from './finance-lib';
import {
  Band,
  BasisFooter,
  Bullet,
  BulletScaleLegend,
  RemovedTile,
  Tile,
} from './finance-chrome';
import { REPORTED_MONTH, type FinanceData, type FinanceRow } from './use-finance-data';
import { TerritoryMap } from './territory-map';

export function ExecutiveOverview({
  data,
  onOpenPortfolio,
}: {
  data: FinanceData;
  onOpenPortfolio: (filter?: 'below_target' | 'needs_fix') => void;
}) {
  const monthName = monthLabel(REPORTED_MONTH);
  const t = data.thresholds;
  const rows = data.rows;

  const total = useMemo(() => rollUp(rows), [rows]);

  const accounts = useMemo(
    () => new Set(rows.map((r) => r.account_id ?? `~${r.facility_id}`)).size,
    [rows],
  );

  const hospitalBasis = useMemo(
    () => rows.find((r) => r.is_hospital && r.basis_label)?.basis_label ?? null,
    [rows],
  );

  // Thresholds are a hard dependency: with no band there is nothing honest to
  // colour, so the page says so instead of inventing one.
  if (!t) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-6">
        <RemovedTile
          title="Executive Overview"
          prerequisite={
            'The margin bands are missing from app_settings. Insert ' +
            'finance_margin_target_pct and finance_margin_amber_floor_pct (the COO owns ' +
            'the values) and reload. The page will not band anything against a ' +
            'threshold it invented.'
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-4 pb-10 md:p-6">
      <BandOne data={data} monthName={monthName} />

      <BandTwo
        rows={rows}
        monthName={monthName}
        accounts={accounts}
        thresholds={t}
        hospitalBasis={hospitalBasis}
        onOpenPortfolio={onOpenPortfolio}
      />

      <BandThree rows={rows} thresholds={t} />

      <BandFour rows={rows} thresholds={t} onOpenPortfolio={onOpenPortfolio} />

      <BandFive data={data} />

      <BandSix rows={rows} thresholds={t} total={total} />

      <BandSeven rows={rows} />

      <BandEight data={data} monthName={monthName} />

      <BasisFooter
        monthName={monthName}
        buildings={total.buildings}
        revenue={fmtMoney(total.revenue, { cents: true })}
        subCost={fmtMoney(total.subCost, { cents: true })}
        marginPctText={fmtPct(total.marginPct, 2)}
        reconciliation={
          'Re-measured 2026-08-14 against Finance’s 12 August tie-out of 188 buildings / ' +
          '$857,667.20 / 27.04%. The move is three record changes, not a query change: ' +
          'FAC-0329 DaVita - Frisco went Onboarding → Active at $2,300 (+1 building), ' +
          'FAC-0244 Gateway Endo MOB had its $3,125 rate set (+1), and FAC-0007 Dry Creek ' +
          'Surgery Center closed at $1,295 (−1). FAC-0103 Des Peres Square Surgery Center ' +
          'was auto-closed and restored on 13 August and nets to zero.'
        }
      />
    </div>
  );
}

// ----- 1. Road to the first $1m month (§9) -----------------------------------

function BandOne({ data, monthName }: { data: FinanceData; monthName: string }) {
  const series = data.revenueTrend;
  const v = data.forecastVersion;
  return (
    <Band
      n={1}
      title="Road to the first $1m month"
      subtitle={
        <>
          Monthly revenue against the $1m line — closed actuals, then forecast. Months
          with five hospital billing week-endings are marked: hospitals bill weekly,
          so a five-week month looks like growth that is not there.{' '}
          {v ? (
            <>
              Drawing <strong>{v.label}</strong>, as of {v.asOf} — the newest model on
              file. Manual upload, one after each close; if that date is not the last
              close, this band is a month behind.
            </>
          ) : (
            <>Manual refresh — one upload of the model after each close.</>
          )}
        </>
      }
    >
      {!series ? (
        <RemovedTile
          title="Road to the first $1m month"
          prerequisite={data.revenueTrendBlocker ?? 'The forecast series returned no rows.'}
        />
      ) : (
        <RevenueTrendChart series={series} monthName={monthName} />
      )}
    </Band>
  );
}

function fiveSundays(iso: string): boolean {
  const d = new Date(`${iso.slice(0, 7)}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const month = d.getMonth();
  let n = 0;
  for (let day = new Date(d); day.getMonth() === month; day.setDate(day.getDate() + 1)) {
    if (day.getDay() === 0) n += 1;
  }
  return n === 5;
}

function RevenueTrendChart({
  series,
  monthName,
}: {
  series: { period_month: string; is_actual: boolean; revenue: number }[];
  monthName: string;
}) {
  const max = Math.max(1_000_000, ...series.map((s) => s.revenue));
  const targetPct = (1_000_000 / max) * 100;
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="relative flex h-40 items-end gap-1">
        <span
          className="absolute left-0 right-0 border-t border-dashed border-tcs-text"
          style={{ bottom: `${targetPct}%` }}
          aria-hidden
        />
        {series.map((s) => {
          const h = (s.revenue / max) * 100;
          const five = fiveSundays(s.period_month);
          return (
            <div
              key={s.period_month}
              className="group relative flex-1"
              title={
                `${monthLabel(s.period_month)} — ${fmtMoney(s.revenue)} ` +
                `(${s.is_actual ? 'QBO actual, closed month' : 'forecast'})` +
                (five ? ' · five hospital billing week-endings in this month' : '')
              }
            >
              <div
                className={`w-full rounded-t ${
                  s.is_actual ? 'bg-tcs-text' : 'bg-tcs-border-strong'
                }`}
                style={{ height: `${Math.max(2, h)}%` }}
              />
              {five && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] text-tcs-text-muted">
                  5w
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-tcs-text-muted">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-tcs-text" /> QBO actual
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-tcs-border-strong" /> Forecast
        </span>
        <span>Dashed line = the $1m month</span>
        <span>
          <strong className="text-tcs-text-secondary">5w</strong> = five hospital
          billing week-endings
        </span>
        <span>Reported month: {monthName}</span>
      </div>
    </div>
  );
}

// ----- 2. Where we stand (§1) ------------------------------------------------

function BandTwo({
  rows,
  monthName,
  accounts,
  thresholds,
  hospitalBasis,
  onOpenPortfolio,
}: {
  rows: FinanceRow[];
  monthName: string;
  accounts: number;
  thresholds: MarginThresholds;
  hospitalBasis: string | null;
  onOpenPortfolio: (filter?: 'below_target' | 'needs_fix') => void;
}) {
  const r = useMemo(() => rollUp(rows), [rows]);
  const band = marginBand(r.marginPct, thresholds);

  return (
    <Band
      n={2}
      title={`Where we stand — ${monthName}`}
      subtitle={
        <>
          The contracted book as recorded: Active facilities with a monthly rate, plus
          hospitals at AR Logbook actuals. <strong>{ARR_CAVEAT}</strong>
          {hospitalBasis && <> Hospital figures: {hospitalBasis}.</>}
        </>
      }
      actions={
        <button
          type="button"
          onClick={() => onOpenPortfolio()}
          className="inline-flex items-center gap-1 rounded border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text"
        >
          Open the portfolio <ArrowUpRight className="h-3 w-3" aria-hidden />
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Tile
          label="Monthly book"
          value={fmtMoney(r.revenue)}
          sub={<span className="tabular-nums">{fmtMoney(r.revenue, { cents: true })}</span>}
          hint="Sum of monthly_rate for the reported book, plus hospital AR Logbook actuals. The services line only — management fee, usables and the auto-scrubber lease are out."
        />
        <Tile
          label="ARR (run rate)"
          value={fmtMoneyCompact(r.arr)}
          sub="monthly book × 12"
          hint={ARR_CAVEAT}
        />
        <Tile
          label="Sub cost"
          value={fmtMoney(r.subCost)}
          sub={<span className="tabular-nums">{fmtMoney(r.subCost, { cents: true })}</span>}
          hint="SUM of every ACTIVE service_assignment on the building — a site can carry a primary cleaner, a day porter and a stretcher cleaner at once."
        />
        <Tile
          label="Labour margin"
          value={fmtPct(r.marginPct, 2)}
          tone={band}
          sub={`${bandLabel(band, thresholds)} · ${fmtPp(
            r.marginPct === null ? null : r.marginPct - thresholds.target_pct,
          )} vs target`}
          hint="(revenue − sub cost) ÷ revenue. This is the tie-out figure: the Portfolio header must match it to the cent."
        />
        <Tile
          label="Buildings"
          value={String(r.buildings)}
          sub={
            r.revenueBearing === r.buildings ? (
              `${accounts} accounts`
            ) : (
              <>
                {accounts} accounts · {r.revenueBearing} carry a revenue figure
              </>
            )
          }
          hint="Every building we serve. A hospital stays in the book on the strength of the relationship, not the current service line, so a hospital with nothing separately billed this period is counted here and carries no dollars."
        />
        <Tile
          label="Margin, covered basis"
          value={fmtPct(r.coveredMarginPct, 2)}
          sub={
            r.noCostBuildings > 0 ? (
              <>
                excludes {r.noCostBuildings} building
                {r.noCostBuildings === 1 ? '' : 's'} carrying{' '}
                <span className="tabular-nums">{fmtMoney(r.noCostRevenue)}</span>/mo with
                no recorded cost
              </>
            ) : (
              'every rated building carries a cost'
            )
          }
          hint="The same sum over revenue that HAS a cost against it. A building with no recorded cost is never 100% margin — this tile is what keeps the headline honest instead of quietly moving it."
        />
      </div>

      {r.noCostBuildings > 0 && (
        <p className="mt-3 rounded border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
          Finance’s §1 note said every rated building carried a cost, so the denominator was
          100% of revenue. That is no longer true: <strong>{r.noCostBuildings}</strong>{' '}
          rated buildings carry{' '}
          <span className="tabular-nums">{fmtMoney(r.noCostRevenue, { cents: true })}</span>
          /mo with no active assignment. They are still in the tie-out denominator, and the
          covered-basis tile beside it is the figure without them.
          {r.noRevenueBuildings > 0 && (
            <>
              {' '}
              Separately, {r.noRevenueBuildings} hospital
              {r.noRevenueBuildings === 1 ? '' : 's'} carry no billed revenue this period —
              they are on the page because the relationship is live, and they are not part
              of this fault count.
            </>
          )}{' '}
          <button
            type="button"
            onClick={() => onOpenPortfolio('needs_fix')}
            className="underline"
          >
            See which ones
          </button>
          .
        </p>
      )}
    </Band>
  );
}

// ----- 3. ARR by territory (§3 §4 §5) ----------------------------------------

function BandThree({
  rows,
  thresholds,
}: {
  rows: FinanceRow[];
  thresholds: MarginThresholds;
}) {
  const byTerritory = useMemo(() => groupBy(rows, (r) => r.territory ?? 'Unmapped'), [rows]);
  const byRegion = useMemo(
    () => groupBy(rows, (r) => (r.market_region ?? 'Unmapped').replace(' Metro', '')),
    [rows],
  );
  const matrix = useMemo(
    () => groupBy(rows, (r) => `${r.territory ?? 'Unmapped'}|${r.service_line}`),
    [rows],
  );
  const unmapped = byTerritory.get('Unmapped') ?? [];

  // The map reads the SAME roll-up the cards do. Computing it twice is how a
  // shape and its card end up telling two different stories.
  const mapSlices = useMemo(
    () =>
      TERRITORY_ORDER.map((terr) => {
        const r = rollUp(byTerritory.get(terr) ?? []);
        return { territory: terr, buildings: r.buildings, arr: r.arr, marginPct: r.marginPct };
      }),
    [byTerritory],
  );

  return (
    <Band
      n={3}
      title="Annual recurring revenue by territory"
      subtitle={
        <>
          Colour is <strong>territory, not margin</strong> — margin lives in the
          percentages and the tables below. Omaha and St. Louis roll into Kansas
          City. {ARR_CAVEAT}
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {TERRITORY_ORDER.map((terr) => {
          const set = byTerritory.get(terr) ?? [];
          const r = rollUp(set);
          const band = marginBand(r.marginPct, thresholds);
          const style = TERRITORY_STYLES[terr];
          return (
            <div
              key={terr}
              className={`rounded-lg border border-l-4 border-tcs-border bg-tcs-raised p-3 ${style.borderClass}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-3 w-3 shrink-0 rounded-sm ${style.swatchClass}`}
                  aria-hidden
                />
                <span className="text-sm font-medium text-tcs-text">{terr}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-tcs-text-muted">
                {TERRITORY_COLOUR_NAME[terr]} · {TERRITORY_STATES[terr].join(' · ')}
              </p>
              <dl className="mt-2 space-y-1 text-xs">
                <Row k="ARR" v={fmtMoneyCompact(r.arr)} />
                <Row k="Gross profit / yr" v={fmtMoneyCompact(r.revenue * 12 - r.subCost * 12)} />
                <Row
                  k="Labour margin"
                  v={fmtPct(r.marginPct)}
                  valueClass={BAND_CLASSES[band].text}
                />
                <Row k="Buildings" v={String(r.buildings)} />
              </dl>
            </div>
          );
        })}
      </div>

      {unmapped.length > 0 && (
        <p className="mt-3 rounded border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
          <strong>{unmapped.length}</strong> building
          {unmapped.length === 1 ? '' : 's'} carry a rate but no territory — a{' '}
          <code>market_region</code> nobody mapped:{' '}
          {unmapped
            .map((u) => `${u.facility_id} ${u.facility_name ?? ''} (${u.market_region ?? 'NULL'})`)
            .join('; ')}
          . Their revenue is still in every total; only the territory split is short.
        </p>
      )}

      <TerritoryMap slices={mapSlices} />

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-tcs-text">By region</h3>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            Buildings, not accounts. A cost-group child carries no assignment of its own
            because the parent holds the whole agreement — its revenue stays in the
            denominator. Dropping it craters any region containing a cost group.
          </p>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                <th className="py-1 font-medium">Region</th>
                <th className="py-1 text-right font-medium">Buildings</th>
                <th className="py-1 text-right font-medium">Revenue / mo</th>
                <th className="py-1 text-right font-medium">Margin</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byRegion.entries())
                .map(([region, set]) => ({ region, r: rollUp(set) }))
                .sort((a, b) => b.r.revenue - a.r.revenue)
                .map(({ region, r }) => (
                  <tr key={region} className="border-b border-tcs-border/60">
                    <td className="py-1 text-tcs-text">{region}</td>
                    <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                      {r.buildings}
                    </td>
                    <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                      {fmtMoney(r.revenue)}
                    </td>
                    <td
                      className={`py-1 text-right tabular-nums ${
                        BAND_CLASSES[marginBand(r.marginPct, thresholds)].text
                      }`}
                    >
                      {fmtPct(r.marginPct)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="text-sm font-medium text-tcs-text">Territory × service line</h3>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            The matrix. Where the same service line reads very differently in two
            territories, the difference is the conversation.
          </p>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                <th className="py-1 font-medium">Territory</th>
                {SERVICE_LINE_ORDER.map((sl) => (
                  <th key={sl} className="py-1 text-right font-medium">
                    {sl}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TERRITORY_ORDER.map((terr) => (
                <tr key={terr} className="border-b border-tcs-border/60">
                  <td className="py-1 text-tcs-text">{terr}</td>
                  {SERVICE_LINE_ORDER.map((sl) => {
                    const set = matrix.get(`${terr}|${sl}`) ?? [];
                    if (set.length === 0) {
                      return (
                        <td key={sl} className="py-1 text-right text-tcs-text-muted">
                          —
                        </td>
                      );
                    }
                    const r = rollUp(set);
                    return (
                      <td
                        key={sl}
                        className={`py-1 text-right tabular-nums ${
                          BAND_CLASSES[marginBand(r.marginPct, thresholds)].text
                        }`}
                        title={`${set.length} buildings · ${fmtMoneyCompact(r.arr)} ARR`}
                      >
                        {fmtPct(r.marginPct)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Band>
  );
}

function Row({
  k,
  v,
  valueClass,
}: {
  k: string;
  v: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-tcs-text-muted">{k}</dt>
      <dd className={`tabular-nums ${valueClass ?? 'text-tcs-text'}`}>{v}</dd>
    </div>
  );
}

// ----- 4. Where the margin sits (§2 §3) --------------------------------------

function BandFour({
  rows,
  thresholds,
  onOpenPortfolio,
}: {
  rows: FinanceRow[];
  thresholds: MarginThresholds;
  onOpenPortfolio: (filter?: 'below_target' | 'needs_fix') => void;
}) {
  const byLine = useMemo(() => groupBy(rows, (r) => r.service_line), [rows]);
  const byTerritory = useMemo(() => groupBy(rows, (r) => r.territory ?? 'Unmapped'), [rows]);

  // Three-band split BY ACCOUNT. An account nets its buildings before comparing
  // to target — that is why building figures do not sum to the account figure.
  const byAccount = useMemo(() => groupBy(rows, (r) => r.account_id ?? `~${r.facility_id}`), [rows]);
  const accountRolls = useMemo(
    () =>
      Array.from(byAccount.entries()).map(([id, set]) => ({
        id,
        name: set[0]?.account_name ?? set[0]?.facility_name ?? id,
        r: rollUp(set),
      })),
    [byAccount],
  );

  const buckets = useMemo(() => {
    const out = { green: [] as typeof accountRolls, amber: [] as typeof accountRolls, red: [] as typeof accountRolls, unknown: [] as typeof accountRolls };
    for (const a of accountRolls) out[marginBand(a.r.marginPct, thresholds)].push(a);
    return out;
  }, [accountRolls, thresholds]);

  const fixList = useMemo(
    () =>
      sumToTarget(
        accountRolls.map((a) =>
          dollarsPerYearToTarget(a.r.revenue, a.r.marginPct, thresholds),
        ),
      ),
    [accountRolls, thresholds],
  );

  const all = useMemo(() => rollUp(rows), [rows]);

  return (
    <Band
      n={4}
      title="Where the margin sits"
      subtitle="Service line and territory as bullets against target, one shared scale, then the three-band split by account with an All accounts subtotal."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
          <h3 className="text-sm font-medium text-tcs-text">By service line</h3>
          {SERVICE_LINE_ORDER.filter((sl) => byLine.has(sl)).map((sl) => {
            const r = rollUp(byLine.get(sl) ?? []);
            return (
              <Bullet
                key={sl}
                label={
                  <>
                    {sl}{' '}
                    <span className="text-tcs-text-muted">
                      {fmtMoneyCompact(r.revenue)}/mo
                    </span>
                  </>
                }
                marginPctValue={r.marginPct}
                thresholds={thresholds}
                right={
                  <>
                    {fmtPct(r.marginPct)}{' '}
                    <span className="text-tcs-text-muted">
                      {fmtPp(r.marginPct === null ? null : r.marginPct - thresholds.target_pct)}
                    </span>
                  </>
                }
              />
            );
          })}
          <BulletScaleLegend thresholds={thresholds} />
        </div>

        <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
          <h3 className="text-sm font-medium text-tcs-text">By territory</h3>
          {TERRITORY_ORDER.filter((terr) => byTerritory.has(terr)).map((terr) => {
            const r = rollUp(byTerritory.get(terr) ?? []);
            return (
              <Bullet
                key={terr}
                label={
                  <span className="flex items-center gap-1.5">
                    <span
                      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${TERRITORY_STYLES[terr].swatchClass}`}
                      aria-hidden
                    />
                    {terr}
                  </span>
                }
                marginPctValue={r.marginPct}
                thresholds={thresholds}
                right={
                  <>
                    {fmtPct(r.marginPct)}{' '}
                    <span className="text-tcs-text-muted">
                      {fmtPp(r.marginPct === null ? null : r.marginPct - thresholds.target_pct)}
                    </span>
                  </>
                }
              />
            );
          })}
          <BulletScaleLegend thresholds={thresholds} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {(['green', 'amber', 'red', 'unknown'] as const).map((b) => {
          const set = buckets[b];
          const rev = set.reduce((a, x) => a + x.r.revenue, 0);
          return (
            <div
              key={b}
              className={`rounded-lg border p-3 ${BAND_CLASSES[b].border} ${BAND_CLASSES[b].bg}`}
            >
              <div className={`font-mono text-[10px] uppercase tracking-wide ${BAND_CLASSES[b].text}`}>
                {bandLabel(b, thresholds)}
              </div>
              <div className={`mt-1 text-lg font-semibold tabular-nums ${BAND_CLASSES[b].text}`}>
                {set.length} account{set.length === 1 ? '' : 's'}
              </div>
              <div className="mt-0.5 text-xs tabular-nums text-tcs-text-secondary">
                {fmtMoney(rev)}/mo
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2">
        <div className="text-xs text-tcs-text-secondary">
          <strong className="text-tcs-text">All accounts:</strong>{' '}
          <span className="tabular-nums">
            {accountRolls.length} accounts · {fmtMoney(all.revenue)}/mo ·{' '}
            {fmtPct(all.marginPct, 2)}
          </span>
        </div>
        <div
          className="text-xs text-tcs-text-secondary"
          title={
            'Sum of (monthly revenue × 12 × (target − margin) ÷ 100) over ONLY the ' +
            'accounts below target. A surplus on an account above target is never netted ' +
            'against it — this is the size of the fix list, not a loss.'
          }
        >
          <strong className="text-tcs-text">$/yr to reach target:</strong>{' '}
          <span className="cursor-help tabular-nums underline decoration-dotted">
            {fmtMoney(fixList)}
          </span>{' '}
          — the size of the fix list, not a loss.{' '}
          <button type="button" onClick={() => onOpenPortfolio('below_target')} className="underline">
            Work it
          </button>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-tcs-text-muted">{LEVEL_MIX_NOTE}</p>
    </Band>
  );
}

// ----- 5. Hospitals — the weekly run rate (§10) -------------------------------

function BandFive({ data }: { data: FinanceData }) {
  const weeks = data.hospitalWeeks;
  return (
    <Band
      n={5}
      title="Hospitals — the weekly run rate"
      subtitle={
        <>
          Recurring dollars per billed week. Filtered to{' '}
          <code>source = &apos;ar_logbook&apos;</code> and the TOTAL rows only:{' '}
          <code>antonela_hour_report</code> writes the same weeks broken out by
          department, so summing the whole table double-counts. Five-week months are
          marked because they look like growth that is not there.
        </>
      }
    >
      {!weeks ? (
        <RemovedTile
          title="Hospital weekly run rate"
          prerequisite={
            data.hospitalWeeksBlocker ??
            'No ar_logbook weeks are loaded into client_billing_lines.'
          }
        />
      ) : (
        <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                <th className="py-1 font-medium">Week ending</th>
                <th className="py-1 text-right font-medium">Billed</th>
                <th className="py-1 text-right font-medium">Hospitals</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week_ending} className="border-b border-tcs-border/60">
                  <td className="py-1 tabular-nums text-tcs-text">{w.week_ending}</td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                    {fmtMoney(w.billed_that_week, { cents: true })}
                  </td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                    {w.hospitals}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 rounded border border-st-warn-border bg-st-warn-bg px-2 py-1.5 text-[11px] text-st-warn-fg">
            {weeks.length} week{weeks.length === 1 ? '' : 's'} loaded. The AR Logbook load
            stopped after week-ending 2026-06-28 and 2026-06-07 is missing; there is
            nothing from July onward. Hospital revenue in the tiles above is therefore the
            July 2026 AR Logbook actual. Hospital billing is a MANUAL upload into
            client_billing_lines and there is no QBO feed — what this band needs is the
            missing weeks loaded, not a connector.
          </p>
        </div>
      )}
    </Band>
  );
}

// ----- 6. Revenue by account manager (§6) -------------------------------------

function BandSix({
  rows,
  thresholds,
  total,
}: {
  rows: FinanceRow[];
  thresholds: MarginThresholds;
  total: ReturnType<typeof rollUp>;
}) {
  const byManager = useMemo(
    () => groupBy(rows, (r) => r.account_manager_name ?? 'Unassigned'),
    [rows],
  );
  const cards = useMemo(
    () =>
      Array.from(byManager.entries())
        .map(([manager, set]) => ({ manager, set, r: rollUp(set) }))
        .sort((a, b) => b.r.arr - a.r.arr),
    [byManager],
  );
  const managerTotal = cards.reduce((a, c) => a + c.r.revenue, 0);
  const unassigned = byManager.get('Unassigned') ?? [];

  return (
    <Band
      n={6}
      title="Revenue by account manager"
      subtitle={
        <>
          Every building sits on exactly one name. {MANAGER_VIEW_NOTE} Managers are always
          named — this page never prints a count of AMs.
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(({ manager, set, r }) => {
          const band = marginBand(r.marginPct, thresholds);
          const fix = dollarsPerYearToTarget(r.revenue, r.marginPct, thresholds);
          return (
            <div key={manager} className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-tcs-text">{manager}</span>
                <span className={`text-sm font-medium tabular-nums ${BAND_CLASSES[band].text}`}>
                  {fmtPct(r.marginPct)}
                </span>
              </div>
              <dl className="mt-2 space-y-1 text-xs">
                <Row k="ARR" v={fmtMoneyCompact(r.arr)} />
                <Row k="Revenue / mo" v={fmtMoney(r.revenue)} />
                <Row k="Gross profit / yr" v={fmtMoneyCompact((r.revenue - r.subCost) * 12)} />
                <Row
                  k="Buildings"
                  v={`${r.buildings} across ${new Set(set.map((s) => s.account_id)).size} accounts`}
                />
              </dl>
              {fix && (
                <p
                  className="mt-2 cursor-help text-[11px] text-tcs-text-secondary underline decoration-dotted"
                  title={fix.workings}
                >
                  {fmtMoney(fix.amount)}/yr to reach target — the size of the fix list, not
                  a loss.
                </p>
              )}
              {r.noCostBuildings > 0 && (
                <p className="mt-1 text-[11px] text-st-warn-fg">
                  {r.noCostBuildings} building{r.noCostBuildings === 1 ? '' : 's'} with no
                  recorded cost — {fmtPct(r.coveredMarginPct)} on the covered basis.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-tcs-text-muted">
        Manager cards total{' '}
        <span className="tabular-nums">{fmtMoney(managerTotal, { cents: true })}</span>/mo
        against the account view&apos;s{' '}
        <span className="tabular-nums">{fmtMoney(total.revenue, { cents: true })}</span>/mo.{' '}
        {managerTotal === total.revenue
          ? 'They agree here because no account is currently split across two managers at a rate that nets.'
          : 'The difference is real: slices that net out inside a shared account do not net out across two managers.'}
      </p>

      {unassigned.length > 0 && (
        <p className="mt-2 rounded border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
          <strong>Pending correct assignment.</strong> {unassigned.length} building
          {unassigned.length === 1 ? '' : 's'} carry no{' '}
          <code>account_manager_eid</code>:{' '}
          {unassigned.map((u) => `${u.facility_id} ${u.facility_name ?? ''}`).join('; ')}.
          They sit in this note rather than on a card, because nothing in the app records a
          named manager <em>agreeing</em> to their own list —{' '}
          <code>last_confirmed_at</code> is a general record-review stamp. An{' '}
          <code>am_confirmed_at</code> / <code>am_confirmed_by</code> pair only the named
          manager can set would close it.
        </p>
      )}
    </Band>
  );
}

// ----- 7. Money on the table (§7 §8) ------------------------------------------

function BandSeven({ rows }: { rows: FinanceRow[] }) {
  const today = new Date();
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + 60);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const due = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            r.next_kicker_date !== null &&
            r.next_kicker_date >= iso(today) &&
            r.next_kicker_date <= iso(horizon),
        )
        .sort((a, b) => (a.next_kicker_date ?? '').localeCompare(b.next_kicker_date ?? '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows],
  );

  // Hospitals are EXCLUDED from the missing-escalation-date figure. Every one of
  // the nine carries a NULL next_kicker_date because they do not bill a monthly
  // rate at all — counting them turns a $451,960 / 52%-of-the-book alarm into
  // something a board would rightly dismiss, and buries the real 53-building
  // data fix inside it. Jose's own verified figure (57 buildings / $118,840) was
  // measured on the non-hospital book, which is the honest denominator here.
  const noDate = useMemo(
    () => rows.filter((r) => r.next_kicker_date === null && !r.is_hospital),
    [rows],
  );
  const noDateRevenue = noDate.reduce((a, r) => a + r.revenue, 0);
  const bookRevenue = rows.filter((r) => !r.is_hospital).reduce((a, r) => a + r.revenue, 0);
  const hospitalsNoDate = rows.filter((r) => r.next_kicker_date === null && r.is_hospital).length;
  const dueUplift = due.reduce((a, r) => a + r.revenue * 12 * 0.03, 0);

  const byAccount = useMemo(() => groupBy(rows, (r) => r.account_id ?? `~${r.facility_id}`), [rows]);
  const conc = useMemo(() => {
    const list = Array.from(byAccount.entries()).map(([id, set]) => ({
      id,
      name: set[0]?.account_name ?? set[0]?.facility_name ?? id,
      revenue: set.reduce((a, s) => a + s.revenue, 0),
      managers: namedManagers(set.map((s) => s.account_manager_name)),
    }));
    const total = list.reduce((a, x) => a + x.revenue, 0) || 1;
    const withShare = list
      .map((x) => ({ ...x, share: (x.revenue / total) * 100 }))
      .sort((a, b) => b.revenue - a.revenue);
    const hhi = Math.round(withShare.reduce((a, x) => a + x.share * x.share, 0));
    const cum = (n: number) =>
      withShare.slice(0, n).reduce((a, x) => a + x.share, 0);
    return { top: withShare.slice(0, 10), hhi, largest: withShare[0]?.share ?? 0, top5: cum(5), top10: cum(10) };
  }, [byAccount]);

  return (
    <Band
      n={7}
      title="Money on the table"
      subtitle="Annual increases falling due in the next 60 days, with the manager who owns the conversation — and the concentration behind the book."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-tcs-text">
            Annual increases due in 60 days
          </h3>
          {due.length === 0 ? (
            <p className="mt-2 text-xs text-tcs-text-secondary">
              Nothing falls due between {iso(today)} and {iso(horizon)}.
            </p>
          ) : (
            <>
              <table className="mt-2 w-full text-xs">
                <thead>
                  <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                    <th className="py-1 font-medium">Facility</th>
                    <th className="py-1 font-medium">Manager</th>
                    <th className="py-1 text-right font-medium">Rate / mo</th>
                    <th className="py-1 text-right font-medium">Uplift / yr @ 3%</th>
                    <th className="py-1 text-right font-medium">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {due.map((r) => (
                    <tr key={r.facility_id} className="border-b border-tcs-border/60">
                      <td className="py-1 text-tcs-text">
                        {r.facility_id} {r.facility_name}
                      </td>
                      <td className="py-1 text-tcs-text-secondary">
                        {r.account_manager_name ?? 'Unassigned'}
                      </td>
                      <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                        {fmtMoney(r.revenue)}
                      </td>
                      <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                        {fmtMoney(r.revenue * 12 * 0.03)}
                      </td>
                      <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                        {r.next_kicker_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-[11px] text-tcs-text-muted">
                {due.length} buildings ·{' '}
                <span className="tabular-nums">
                  {fmtMoney(due.reduce((a, r) => a + r.revenue, 0))}
                </span>
                /mo · <span className="tabular-nums">{fmtMoney(dueUplift)}</span>/yr at 3%.
              </p>
            </>
          )}

          <p className="mt-3 rounded border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
            <strong>The bigger number:</strong> {noDate.length} buildings carry{' '}
            <span className="tabular-nums">{fmtMoney(noDateRevenue)}</span>/mo —{' '}
            {fmtPct((noDateRevenue / (bookRevenue || 1)) * 100)} of the non-hospital book —
            with no{' '}
            <code>next_kicker_date</code> at all, so they cannot be worked from the app.
            Larger than every increase due in the next 60 days combined, and a data fix
            rather than a negotiation.
            {hospitalsNoDate > 0 && (
              <>
                {' '}
                The {hospitalsNoDate} hospitals are excluded from this count and from its
                denominator: they carry no monthly rate at all, so a missing escalation
                date on a hospital is not a data gap.
              </>
            )}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-tcs-text">Concentration</h3>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            Largest account {fmtPct(conc.largest)} · top five {fmtPct(conc.top5)} · top ten{' '}
            {fmtPct(conc.top10)} · Herfindahl index {conc.hhi}.
          </p>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                <th className="py-1 font-medium">Account</th>
                <th className="py-1 font-medium">Manager</th>
                <th className="py-1 text-right font-medium">Revenue / mo</th>
                <th className="py-1 text-right font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {conc.top.map((a) => (
                <tr key={a.id} className="border-b border-tcs-border/60">
                  <td className="py-1 text-tcs-text">{a.name}</td>
                  <td className="py-1 text-tcs-text-secondary">{a.managers}</td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                    {fmtMoney(a.revenue)}
                  </td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                    {fmtPct(a.share)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Band>
  );
}

// ----- 8. Where the data still fails (§11 §12) --------------------------------

function BandEight({ data, monthName }: { data: FinanceData; monthName: string }) {
  const byCheck = useMemo(
    () => groupBy(data.dataQuality, (r) => r.check_key),
    [data.dataQuality],
  );

  // A paused HOSPITAL is excluded from the contracted book -- it has no monthly
  // rate to exclude -- and is STILL shown, because pause is not termination and
  // the relationship is what puts a hospital on this page (Ben, 2026-08-20).
  // Listing it flatly under "excluded" contradicted the roster above it.
  const shownIds = useMemo(() => new Set(data.rows.map((r) => r.facility_id)), [data.rows]);

  return (
    <Band
      n={8}
      title="Where the data still fails"
      subtitle={`What is excluded and why, what the refresh checks caught, and the tiles this page cannot fill for ${monthName}. Every count below is measured on load, not asserted.`}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-tcs-text">Refresh checks</h3>
          {byCheck.size === 0 ? (
            <p className="mt-2 text-xs text-st-ok-fg">
              All checks clean — no rated building without a cost, no unmapped territory,
              no missing manager, no implausible margin, no missing increase date.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {Array.from(byCheck.entries()).map(([key, items]) => (
                <li key={key} className="rounded border border-st-warn-border bg-st-warn-bg p-2">
                  <div className="text-xs font-medium text-st-warn-fg">
                    {items[0].check_label} — {items.length}
                  </div>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[11px] text-tcs-text-secondary">
                      Name them
                    </summary>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-tcs-text-secondary">
                      {items.slice(0, 60).map((i) => (
                        <li key={`${key}-${i.facility_id}`}>
                          <span className="text-tcs-text">
                            {i.facility_id} {i.facility_name}
                          </span>{' '}
                          — {i.detail}
                        </li>
                      ))}
                      {items.length > 60 && (
                        <li className="text-tcs-text-muted">
                          …and {items.length - 60} more. The full list is{' '}
                          <code>public.v_finance_data_quality</code>.
                        </li>
                      )}
                    </ul>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium text-tcs-text">
            Excluded from the reported book — by decision, not data quality
          </h3>
          <ul className="mt-2 space-y-1 text-[11px] text-tcs-text-secondary">
            {data.exclusions.map((x) => (
              <li key={x.facility_id}>
                <span className="text-tcs-text">{x.facility_id}</span> — {x.exclusion_reason}
                {shownIds.has(x.facility_id) && (
                  <span className="ml-1 rounded bg-st-info-bg px-1 text-[9px] text-st-info-fg">
                    still counted above — excluded from the contracted book, not from the
                    portfolio
                  </span>
                )}
              </li>
            ))}
          </ul>

          <h3 className="mt-4 text-sm font-medium text-tcs-text">
            Starting this month — shown separately, never at zero cost
          </h3>
          {data.futureStarts.length === 0 ? (
            <p className="mt-1 text-[11px] text-tcs-text-muted">None.</p>
          ) : (
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                  <th className="py-1 font-medium">Facility</th>
                  <th className="py-1 text-right font-medium">Rate / mo</th>
                  <th className="py-1 text-right font-medium">Sub cost</th>
                  <th className="py-1 text-right font-medium">Starts</th>
                </tr>
              </thead>
              <tbody>
                {data.futureStarts.map((f) => (
                  <tr key={f.facility_id} className="border-b border-tcs-border/60">
                    <td className="py-1 text-tcs-text">
                      {f.facility_id} {f.facility_name}
                    </td>
                    <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                      {f.revenue === null ? 'not recorded' : fmtMoney(f.revenue)}
                    </td>
                    <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                      {f.sub_cost === null ? 'not recorded' : fmtMoney(f.sub_cost)}
                    </td>
                    <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                      {f.planned_start_date ?? f.contract_start ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-1 text-[11px] text-tcs-text-muted">
            &quot;Not recorded&quot; is not $0. A blank cost here means nobody has entered
            one yet, and showing these at zero cost would put them on the page at 100%
            margin.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <RemovedTile
          title="AR aging / DSO / CEI"
          prerequisite={
            'Revenue by facility on a BILLED basis. invoices holds 2 rows and ' +
            'invoice_line_items is empty, so there is nothing to age. Loading the ' +
            'transaction report Finance already produces into invoices + invoice_line_items ' +
            'fills all three at once. No connector is required and none is being built.'
          }
        />
        <RemovedTile
          title="Book versus actual bridge"
          prerequisite={
            'Actual sub cost per facility per month. partner_payments source ' +
            'jose_ap_report stalled after mid-May 2026 — cycles run 4 Jan to 15 May and ' +
            'June/July/August hold project rows only. Job 2 (restart the AP recurring ' +
            'load) is the whole fix, plus a period_month column so a month of sub cost is ' +
            'a GROUP BY rather than a join through payroll_cycles that nobody remembers ' +
            'to write.'
          }
        />
        <RemovedTile
          title="Sub cost by month"
          prerequisite={
            'The same Job 2 restart. week_ending is NULL on every recurring ' +
            'partner_payments row, so the period is only recoverable through cycle_id → ' +
            'payroll_cycles.period_start / period_end.'
          }
        />
      </div>
    </Band>
  );
}
